import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import * as XLSX from 'xlsx';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useAuth } from '@/shared/AuthContext';
import { ExpiryStatusBadge } from '@/shared/ExpiryStatusBadge';
import type { ExpiryStatus } from '@vvf/shared';

// Stato scadenza: stesso tipo di @vvf/shared (alias locale per leggibilita').
type StatusValue = ExpiryStatus;

interface DeadlineRow {
  userId: string;
  nickname: string;
  tenantName: string;
  // Campi data nullable: il backend ammette record parziali (F14), possono mancare.
  medical?: { lastCheckDate?: string | null; expiryDate?: string | null; sixMonthsBeforeExpiry?: string | null } | null;
  medicalStatus: StatusValue;
  license?: { type: string; number?: string | null; issuedAt?: string | null; expiryDate?: string | null; sixMonthsBeforeExpiry?: string | null } | null;
  licenseStatus: StatusValue;
}

interface PublicTenant { id: number; name: string }

export function DeadlinesPage(): React.JSX.Element {
  usePageTitle('Scadenze Utenti');
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<DeadlineRow | null>(null);
  const { user } = useAuth();

  const { data } = useQuery<DeadlineRow[]>({
    queryKey: ['deadlines'],
    queryFn: async () => (await api.get('/deadlines')).data,
  });

  const { data: tenants } = useQuery<PublicTenant[]>({
    queryKey: ['tenants.public'],
    queryFn: async () => (await api.get('/tenants/public')).data,
  });

  // VvfUser espone `homeTenantId` (number), non `tenantId`: combacia con PublicTenant.id (number).
  const stationName = user ? tenants?.find(t => t.id === user.homeTenantId)?.name ?? '' : '';

  async function importXlsx(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: Record<string, string | number>[] = XLSX.utils.sheet_to_json(ws);
      let ok = 0, fail = 0;
      for (const row of rows) {
        const nickname = String(row.Nickname ?? '').trim().toLowerCase();
        if (!nickname) continue;
        const u = (data ?? []).find(x => x.nickname.toLowerCase() === nickname);
        if (!u) { fail++; continue; }
        try {
          await api.put('/deadlines', {
            userId: u.userId,
            medicalLastCheck: parseXlsxDate(row['Visita Medica']),
            medicalExpiry: parseXlsxDate(row['Scadenza Visita']),
            medicalSixMonthsBefore: parseXlsxDate(row['Scad -6m Visita']),
            licenseType: row.Patente || null,
            licenseExpiry: parseXlsxDate(row['Scadenza Patente']),
            licenseSixMonthsBefore: parseXlsxDate(row['Scad -6m Patente']),
          });
          ok++;
        } catch { fail++; }
      }
      qc.invalidateQueries({ queryKey: ['deadlines'] });
      toast.show(`Import completato: ${ok} ok, ${fail} errori`);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  }

  function exportXlsx() {
    if (!data) return;
    const rows = data.map(r => ({
      Nickname: r.nickname,
      Distaccamento: r.tenantName,
      'Visita Medica': r.medical?.lastCheckDate ?? '',
      'Scad -6m Visita': r.medical?.sixMonthsBeforeExpiry ?? '',
      'Scadenza Visita': r.medical?.expiryDate ?? '',
      'Stato Visita': r.medicalStatus,
      Patente: r.license?.type ?? '',
      'Scad -6m Patente': r.license?.sixMonthsBeforeExpiry ?? '',
      'Scadenza Patente': r.license?.expiryDate ?? '',
      'Stato Patente': r.licenseStatus,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Scadenze');
    XLSX.writeFile(wb, `scadenze_utenti_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  return (
    <div className="page-container">
      {/* Header come legacy: titolo + sottotitolo distaccamento + bottone "Torna Indietro" */}
      <header className="app-page-header">
        <div>
          <h1 className="app-page-title">📅 Scadenze Utenti</h1>
          {stationName && <p className="app-page-subtitle">{stationName}</p>}
        </div>
        <button className="btn-back" onClick={() => navigate('/dashboard')}>← Torna Indietro</button>
      </header>

      {/* Action buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
        <button onClick={exportXlsx} className="btn-success"
          style={{ padding: '10px 20px', borderRadius: 8, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          📥 Esporta XLSX
        </button>
        <label style={{
          background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
          color: 'white', padding: '10px 20px', borderRadius: 8, fontWeight: 600,
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          {importing ? '⏳ Importazione...' : '📤 Importa XLSX'}
          <input type="file" accept=".xlsx,.xls" onChange={importXlsx} disabled={importing} style={{ display: 'none' }} />
        </label>
      </div>

      {/* Tabella scadenze (10 colonne come legacy) */}
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead>
            <tr>
              <th style={thStyle}>Nome Utente</th>
              <th style={thStyle}>Visita Medica</th>
              <th style={thStyle}>Scad. -6 mesi</th>
              <th style={thStyle}>Scadenza Visita</th>
              <th style={thStyle}>Stato Visita</th>
              <th style={thStyle}>Patente</th>
              <th style={thStyle}>Scad. -6 mesi</th>
              <th style={thStyle}>Scadenza Patente</th>
              <th style={thStyle}>Stato Patente</th>
              <th style={thStyle}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map(r => (
              <tr key={r.userId} className="deadlines-row">
                <td style={tdStyle}><strong>{r.nickname}</strong></td>
                <td style={tdStyle}>{fmt(r.medical?.lastCheckDate)}</td>
                <td style={tdStyle}>{fmt(r.medical?.sixMonthsBeforeExpiry)}</td>
                <td style={tdStyle}>{fmt(r.medical?.expiryDate)}</td>
                <td style={tdStyle}><ExpiryStatusBadge status={r.medicalStatus} variant="deadlines" /></td>
                <td style={tdStyle}>{r.license?.type ?? '-'}</td>
                <td style={tdStyle}>{fmt(r.license?.sixMonthsBeforeExpiry)}</td>
                <td style={tdStyle}>{fmt(r.license?.expiryDate)}</td>
                <td style={tdStyle}><ExpiryStatusBadge status={r.licenseStatus} variant="deadlines" /></td>
                <td style={tdStyle}>
                  <button onClick={() => setEditing(r)}
                    style={{
                      background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                      color: 'white', border: 'none', padding: '6px 12px',
                      borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    }}>
                    ✎ Modifica
                  </button>
                </td>
              </tr>
            ))}
            {(data ?? []).length === 0 && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                Nessun utente trovato
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <DeadlinesEditModal row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['deadlines'] });
            setEditing(null);
            toast.show('Scadenze aggiornate');
          }} />
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd',
  fontSize: 13, fontWeight: 600, color: '#333',
  background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10,
};
const tdStyle: React.CSSProperties = {
  padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd', fontSize: 13,
};

function fmt(s?: string | null): string {
  if (!s) return '-';
  // Mostra come DD/MM/YYYY
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

function DeadlinesEditModal({ row, onClose, onSaved }: {
  row: DeadlineRow;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const [medicalLast, setMedicalLast] = useState(row.medical?.lastCheckDate ?? '');
  const [medicalSix, setMedicalSix] = useState(row.medical?.sixMonthsBeforeExpiry ?? '');
  const [medicalExp, setMedicalExp] = useState(row.medical?.expiryDate ?? '');
  const [licenseType, setLicenseType] = useState(row.license?.type ?? '');
  const [licenseNumber, setLicenseNumber] = useState(row.license?.number ?? '');
  const [licenseIssue, setLicenseIssue] = useState(row.license?.issuedAt ?? '');
  const [licenseSix, setLicenseSix] = useState(row.license?.sixMonthsBeforeExpiry ?? '');
  const [licenseExp, setLicenseExp] = useState(row.license?.expiryDate ?? '');

  // Il Tipo patente e' l'unico campo obbligatorio della sezione patente (chiave UserId+Type).
  // Se l'utente compila altri campi patente senza il tipo, lo segnaliamo inline.
  const hasLicenseData = !!(licenseNumber || licenseIssue || licenseSix || licenseExp);
  const licenseTypeMissing = hasLicenseData && !licenseType.trim();

  async function save() {
    if (licenseTypeMissing) {
      toast.show('Indica il Tipo Patente per salvare i dati della patente', 'error');
      return;
    }
    try {
      await api.put('/deadlines', {
        userId: row.userId,
        medicalLastCheck: medicalLast || null,
        medicalSixMonthsBefore: medicalSix || null,
        medicalExpiry: medicalExp || null,
        licenseType: licenseType || null,
        licenseNumber: licenseNumber || null,
        // Allineato al DTO backend (LicenseIssued): il vecchio nome `licenseIssue`
        // non veniva mappato e la data rilascio non si salvava.
        licenseIssued: licenseIssue || null,
        licenseSixMonthsBefore: licenseSix || null,
        licenseExpiry: licenseExp || null,
      });
      onSaved();
    } catch {
      toast.show('Errore salvataggio', 'error');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <h2 style={{ marginBottom: 8 }}>Modifica Scadenze</h2>
        <p style={{ color: '#666', marginBottom: 20 }}>{row.nickname}</p>

        <h3 style={{ marginTop: 20, marginBottom: 15, fontSize: 18 }}>Visita Medica</h3>
        <div className="form-group"><label>Data Ultima Visita</label>
          <input type="date" value={medicalLast} onChange={e => setMedicalLast(e.target.value)} /></div>
        <div className="form-group"><label>Data Scadenza -6 mesi</label>
          <input type="date" value={medicalSix} onChange={e => setMedicalSix(e.target.value)} /></div>
        <div className="form-group"><label>Data Scadenza Visita</label>
          <input type="date" value={medicalExp} onChange={e => setMedicalExp(e.target.value)} /></div>

        <h3 style={{ marginTop: 20, marginBottom: 15, fontSize: 18 }}>Patente Principale</h3>
        <div className="form-group"><label>Tipo Patente</label>
          <input type="text" value={licenseType} onChange={e => setLicenseType(e.target.value)} placeholder="Es. B, C, D, etc." />
          {licenseTypeMissing && (
            <span style={{ color: '#dc3545', fontSize: 12, display: 'block', marginTop: 4 }}>
              Il Tipo Patente e' obbligatorio per salvare i dati della patente.
            </span>
          )}</div>
        <div className="form-group"><label>Numero Patente</label>
          <input type="text" value={licenseNumber} onChange={e => setLicenseNumber(e.target.value)} /></div>
        <div className="form-group"><label>Data Rilascio</label>
          <input type="date" value={licenseIssue} onChange={e => setLicenseIssue(e.target.value)} /></div>
        <div className="form-group"><label>Data Scadenza -6 mesi</label>
          <input type="date" value={licenseSix} onChange={e => setLicenseSix(e.target.value)} /></div>
        <div className="form-group"><label>Data Scadenza</label>
          <input type="date" value={licenseExp} onChange={e => setLicenseExp(e.target.value)} /></div>

        <div style={{ display: 'flex', gap: 10, marginTop: 30, flexWrap: 'wrap' }}>
          <button onClick={save} className="btn-success" style={{ flex: 1, minWidth: 120, padding: '10px 20px' }}>Salva</button>
          <button onClick={onClose} className="btn-secondary" style={{ flex: 1, minWidth: 120, padding: '10px 20px' }}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

function parseXlsxDate(v: unknown): string | null {
  if (!v) return null;
  if (typeof v === 'number') {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const d = new Date(excelEpoch.getTime() + v * 86400000);
    return d.toISOString().slice(0, 10);
  }
  if (typeof v === 'string') {
    const s = v.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [, d, mo, y] = m;
      return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  }
  return null;
}
